import { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitleDefault from '../default/DialogTitleDefault';
import {
    Box,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import EmailIcon from '@mui/icons-material/Email';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExploreIcon from '@mui/icons-material/Explore';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import RouterIcon from '@mui/icons-material/Router';
import { ButtonBasic, InputSearchContactNubicom, SelectIvaSoftland, SelectLocalidadesSoftland } from '../../components';
import { toast } from 'react-toastify';
import ApiSoftland from '../../services/apis.softland.service';

const TECNOLOGIA_OPTIONS = [
    { value: 'FO', label: 'Fibra Óptica (FO)' },
    { value: 'RE', label: 'Radio Enlace (RE)' },
];

const CATEGORIA_OPTIONS = [
    { value: 'MAS', label: 'Masivo' },
    { value: 'PYM', label: 'Pyme' },
    { value: 'COR', label: 'Corporativo' },
    { value: 'GOB', label: 'Gobierno' },
];

const INITIAL_FORM = {
    numero_doc: '',
    nombre: '',
    nombre_evento: '',
    email: '',
    telefono: '',
    telefono2: '',
    direccion: '',
    cod_postal: '',
    latitud: '',
    longitud: '',
    cod_categoria: '',
    cod_iva: '',
    tecnologia: '',
};

const INITIAL_ERRORS = Object.fromEntries(Object.keys(INITIAL_FORM).map((k) => [k, false]));

const REQUIRED_FIELDS = [
    'numero_doc',
    'nombre',
    'nombre_evento',
    'direccion',
    'cod_postal',
    'cod_categoria',
    'cod_iva',
    'tecnologia',
];

const FIELD_LABELS = {
    numero_doc:    'N° de Documento',
    nombre:        'Nombre / Razón Social',
    nombre_evento: 'Nombre del Evento',
    direccion:     'Dirección',
    cod_postal:    'Localidad / Código Postal',
    cod_categoria: 'Categoría',
    cod_iva:       'Condición IVA',
    tecnologia:    'Tecnología',
    email:         'Email',
    telefono:      'Teléfono',
    telefono2:     'Teléfono 2',
    latitud:       'Latitud',
    longitud:      'Longitud',
};

const ModalCrearClienteEventual = ({ show, hideModal, onGuardar }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [datosSelect, setDatosSelect] = useState({});
  const handleClose = () => hideModal();

  const handleSelectContact = (data) => {
    if (!data) return;
    setDatosSelect(data);
    setForm((prev) => ({
      ...prev,
      numero_doc:   data?.dni_cuit?.numero          || '',
      nombre:       data?.nombre_completo            || '',
      email:        data?.contacto?.email            || '',
      telefono:     (data?.contacto?.telefono || '').replace(/\D/g, '').slice(0, 10),
      direccion:    data?.ubicacion?.direccion       || '',
      cod_postal:   data?.ubicacion?.codigo_postal   || '',
      latitud:      data?.ubicacion?.latitud         || '',
      longitud:     data?.ubicacion?.longitud        || '',
      cod_iva:      data?.iva?.codigo                || '',
      cod_categoria: data?.categoria?.codigo         || '',
    }));
    setErrors((prev) => ({
      ...prev,
      numero_doc: false, nombre: false, email: false, telefono: false,
      direccion: false, cod_postal: false, latitud: false, longitud: false,
      cod_iva: false, cod_categoria: false,
    }));
  };

  const handleClearCliente = () => {
    setDatosSelect({});
    setForm(INITIAL_FORM);
    setErrors(INITIAL_ERRORS);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validate = () => {
      const newErrors = { ...INITIAL_ERRORS };
      let valid = true;

      REQUIRED_FIELDS.forEach((field) => {
          if (!form[field]?.toString().trim()) {
              newErrors[field] = true;
              valid = false;
          }
      });

      if (form.numero_doc && form.numero_doc.length < 7) {
          newErrors.numero_doc = true;
          valid = false;
      }

      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
          newErrors.email = true;
          valid = false;
      }

      if (form.telefono && form.telefono.length !== 10) {
          newErrors.telefono = true;
          valid = false;
      }

      if (form.telefono2 && form.telefono2.length !== 10) {
          newErrors.telefono2 = true;
          valid = false;
      }

      if ((form.latitud || form.longitud) && !(form.latitud && form.longitud)) {
          newErrors.latitud = !form.latitud;
          newErrors.longitud = !form.longitud;
          valid = false;
      }

      if (form.latitud && form.longitud) {
          const lat = parseFloat(form.latitud);
          const lng = parseFloat(form.longitud);
          const latOk = !isNaN(lat) && lat >= -55.1 && lat <= -21.7;
          const lngOk = !isNaN(lng) && lng >= -73.6 && lng <= -53.6;
          if (!latOk) { newErrors.latitud = true; valid = false; }
          if (!lngOk) { newErrors.longitud = true; valid = false; }
      }

      setErrors(newErrors);
      return { valid, newErrors };
  };

  const onSubmit = async () => {
      const { valid, newErrors } = validate();
      if (!valid) {
          const campos = Object.entries(newErrors)
              .filter(([, hasError]) => hasError)
              .map(([key]) => FIELD_LABELS[key] || key)
              .join(' · ');
          toast.error(`Campos incompletos o inválidos: ${campos}`, { autoClose: 5000 });
          return;
      }
      setIsLoading(true);
      try {
          const payload = {
              numero_doc: form.numero_doc.trim(),
              nombre: form.nombre.trim().toUpperCase(),
              nombre_evento: form.nombre_evento.trim().toUpperCase(),
              email: form.email.trim().toLowerCase(),
              telefono: form.telefono.trim(),
              telefono2: form.telefono2.trim(),
              direccion: form.direccion.trim().toUpperCase(),
              cod_postal: form.cod_postal,
              latitud: form.latitud.trim(),
              longitud: form.longitud.trim(),
              cod_categoria: form.cod_categoria,
              cod_iva: form.cod_iva,
              tecnologia: form.tecnologia,
          };
          await ApiSoftland.createClienteEventual(payload).then((resp) => {
            const nroServicio = resp?.numero_servicio?.trim() || '-';
            const nroCuenta   = resp?.numero_cuenta?.trim()   || '-';
            const segundos    = resp?.segundosFaltantes;
            const msg = `Cliente creado — Servicio: ${nroServicio} · Cuenta: ${nroCuenta}${segundos ? ` · Impacta en ~${segundos}s` : ''}`;
            toast.success(msg, { autoClose: 3500 });
            if (onGuardar) onGuardar();
            handleClose();
          });
      } catch (err) {
          const msg = err?.response?.data?.msg || 'Ocurrió un error al crear el cliente eventual.';
          toast.error(msg);
      } finally {
          setIsLoading(false);
      }
  };

  const inputSx = useMemo(() => ({
      '& .MuiInputLabel-root': { color: 'gray', fontSize: 14 },
      '& fieldset': { borderRadius: 2, border: 1, borderColor: 'gray', height: 45 },
      '& label': { marginTop: -0.5 },
      '& .MuiOutlinedInput-root': {
          color: isDarkMode ? '#fff' : '#000',
          height: 40,
          '&.Mui-focused fieldset': { border: 1, height: 45 },
          '&:hover': { border: 1, borderRadius: 2, borderColor: 'gray', height: 40 },
          '&.Mui-focused:hover': { border: 0, height: 40 },
      },
  }), [isDarkMode]);

  return (
    <Dialog open={show} disableEscapeKeyDown maxWidth='lg' fullWidth sx={{ backdropFilter: 'blur(0.4px)' }}>
        <Box sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
            <DialogTitleDefault id="crear-eventual" onClose={handleClose}>
                <Typography sx={{ flex: 1, textAlign: 'center', color: isDarkMode ? '#fff' : '#000', fontWeight: 'bold' }} variant='h5'>
                    Crear Cliente Eventual
                </Typography>
            </DialogTitleDefault>
            <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
            <DialogContent sx={{ pt: 2 }}>
                <Grid container spacing={2}>

                    {/* ── CARD: CLIENTE ── */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: isDarkMode ? '#263045' : '#f5f7fa',
                            border: `1px solid ${isDarkMode ? '#3d4a62' : '#dde3ed'}`,
                            height: '100%',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Box sx={{ width: 4, height: 20, borderRadius: 1, background: isDarkMode ? '#aac8ff' : '#1976d2' }} />
                                <PersonIcon sx={{ fontSize: 18, color: isDarkMode ? '#aac8ff' : '#1976d2' }} />
                                <Typography sx={{ fontWeight: 'bold', fontSize: 13, color: isDarkMode ? '#aac8ff' : '#1976d2', letterSpacing: 1 }}>
                                    CLIENTE
                                </Typography>
                                <Tooltip title='Limpiar datos del cliente' placement='top'>
                                    <Box
                                        onClick={handleClearCliente}
                                        sx={{
                                            ml: 'auto',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.4,
                                            px: 1,
                                            py: 0.35,
                                            borderRadius: '20px',
                                            cursor: 'pointer',
                                            border: `1px solid ${isDarkMode ? 'rgba(170,200,255,0.25)' : 'rgba(25,118,210,0.25)'}`,
                                            color: isDarkMode ? '#aac8ff' : '#1976d2',
                                            opacity: 0.75,
                                            transition: 'all 0.15s ease',
                                            userSelect: 'none',
                                            '&:hover': {
                                                opacity: 1,
                                                background: isDarkMode ? 'rgba(170,200,255,0.1)' : 'rgba(25,118,210,0.07)',
                                                borderColor: isDarkMode ? '#aac8ff' : '#1976d2',
                                            },
                                        }}
                                    >
                                        <RestartAltIcon sx={{ fontSize: 13 }} />
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.3, lineHeight: 1 }}>
                                            Limpiar
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                            <Grid container spacing={1.5}>
                              <Grid item xs={12}>
                                <InputSearchContactNubicom
                                  soloCuentaSoft
                                  onClick={handleSelectContact}
                                  datosSelect={datosSelect}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                  <Box>
                                      <Typography sx={{ fontSize: 12, color: errors.numero_doc ? '#f44336' : 'gray', mb: 0.3 }}>N° de Documento *</Typography>
                                      <TextField
                                          value={form.numero_doc}
                                          onChange={(e) => setField('numero_doc', e.target.value.replace(/\D/g, ''))}
                                          error={errors.numero_doc}
                                          helperText={errors.numero_doc ? (form.numero_doc && form.numero_doc.length < 7 ? 'Mínimo 7 dígitos' : 'Requerido') : 'DNI (7-8 dígitos) o CUIT (11 dígitos)'}
                                          fullWidth
                                          size='small'
                                          inputProps={{ maxLength: 11, inputMode: 'numeric' }}
                                          InputProps={{ startAdornment: <InputAdornment position='start'><BadgeIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                          sx={inputSx}
                                      />
                                  </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                  <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                          <CategoryIcon sx={{ fontSize: 14, color: errors.cod_categoria ? '#f44336' : 'gray' }} />
                                          <Typography sx={{ fontSize: 12, color: errors.cod_categoria ? '#f44336' : 'gray' }}>Categoría *</Typography>
                                      </Box>
                                      <FormControl fullWidth size='small' error={errors.cod_categoria}>
                                          <Select
                                              value={form.cod_categoria}
                                              onChange={(e) => setField('cod_categoria', e.target.value)}
                                              displayEmpty
                                              sx={{
                                                  height: 40,
                                                  borderRadius: 2,
                                                  color: isDarkMode ? '#fff' : '#000',
                                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: errors.cod_categoria ? '#f44336' : 'gray' },
                                              }}
                                          >
                                              <MenuItem value='' disabled><em style={{ color: 'gray' }}>Seleccionar...</em></MenuItem>
                                              {CATEGORIA_OPTIONS.map((opt) => (
                                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                              ))}
                                          </Select>
                                          {errors.cod_categoria && <FormHelperText>Requerido</FormHelperText>}
                                      </FormControl>
                                  </Box>
                              </Grid>
                              <Grid item xs={12}>
                                  <Box sx={{ '& > div': { marginBottom: 0 } }}>
                                      <Typography sx={{ fontSize: 12, color: errors.cod_iva ? '#f44336' : 'gray', mb: 0.3 }}>Condición IVA *</Typography>
                                      <SelectIvaSoftland
                                          value={form.cod_iva}
                                          onChange={(e) => setField('cod_iva', e?.value || '')}
                                          isInvalid={errors.cod_iva}
                                          filter
                                      />
                                      {errors.cod_iva && (
                                          <Typography sx={{ fontSize: 11, color: '#f44336', mt: 0.3 }}>Requerido</Typography>
                                      )}
                                  </Box>
                              </Grid>
                              <Grid item xs={12}>
                                  <Box>
                                      <Typography sx={{ fontSize: 12, color: errors.nombre ? '#f44336' : 'gray', mb: 0.3 }}>Nombre / Razón Social *</Typography>
                                      <TextField
                                          value={form.nombre}
                                          onChange={(e) => setField('nombre', e.target.value)}
                                          error={errors.nombre}
                                          helperText={errors.nombre ? 'Requerido' : ''}
                                          fullWidth
                                          size='small'
                                          inputProps={{ maxLength: 60 }}
                                          InputProps={{ startAdornment: <InputAdornment position='start'><BusinessIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                          sx={inputSx}
                                      />
                                  </Box>
                              </Grid>
                              <Grid item xs={12}>
                                  <Box>
                                      <Typography sx={{ fontSize: 12, color: errors.email ? '#f44336' : 'gray', mb: 0.3 }}>Email</Typography>
                                      <TextField
                                          value={form.email}
                                          onChange={(e) => setField('email', e.target.value)}
                                          error={errors.email}
                                          helperText={errors.email ? 'Ingresá un correo electrónico válido' : ''}
                                          fullWidth
                                          size='small'
                                          inputProps={{ maxLength: 100 }}
                                          InputProps={{ startAdornment: <InputAdornment position='start'><EmailIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                          sx={inputSx}
                                      />
                                  </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                  <Box>
                                      <Typography sx={{ fontSize: 12, color: errors.telefono ? '#f44336' : 'gray', mb: 0.3 }}>Teléfono</Typography>
                                      <TextField
                                          value={form.telefono}
                                          onChange={(e) => setField('telefono', e.target.value.replace(/\D/g, ''))}
                                          error={errors.telefono}
                                          helperText={errors.telefono ? '10 dígitos requeridos' : '10 dígitos'}
                                          fullWidth
                                          size='small'
                                          inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                          InputProps={{ startAdornment: <InputAdornment position='start'><LocalPhoneIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                          sx={inputSx}
                                      />
                                  </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                  <Box>
                                      <Typography sx={{ fontSize: 12, color: errors.telefono2 ? '#f44336' : 'gray', mb: 0.3 }}>Teléfono 2</Typography>
                                      <TextField
                                          value={form.telefono2}
                                          onChange={(e) => setField('telefono2', e.target.value.replace(/\D/g, ''))}
                                          error={errors.telefono2}
                                          helperText={errors.telefono2 ? '10 dígitos requeridos' : '10 dígitos'}
                                          fullWidth
                                          size='small'
                                          inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                          InputProps={{ startAdornment: <InputAdornment position='start'><PhoneAndroidIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                          sx={inputSx}
                                      />
                                  </Box>
                              </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* ── CARD: EVENTO ── */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: isDarkMode ? '#263045' : '#f5f7fa',
                            border: `1px solid ${isDarkMode ? '#3d4a62' : '#dde3ed'}`,
                            height: '100%',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Box sx={{ width: 4, height: 20, borderRadius: 1, background: isDarkMode ? '#a5d6a7' : '#2e7d32' }} />
                                <EmojiEventsIcon sx={{ fontSize: 18, color: isDarkMode ? '#a5d6a7' : '#2e7d32' }} />
                                <Typography sx={{ fontWeight: 'bold', fontSize: 13, color: isDarkMode ? '#a5d6a7' : '#2e7d32', letterSpacing: 1 }}>
                                    EVENTO
                                </Typography>
                            </Box>
                            <Grid container spacing={1.5}>

                                <Grid item xs={12}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: errors.nombre_evento ? '#f44336' : 'gray', mb: 0.3 }}>Nombre del Evento *</Typography>
                                        <TextField
                                            value={form.nombre_evento}
                                            onChange={(e) => setField('nombre_evento', e.target.value)}
                                            error={errors.nombre_evento}
                                            helperText={errors.nombre_evento ? 'Requerido' : ''}
                                            fullWidth
                                            size='small'
                                            inputProps={{ maxLength: 60 }}
                                            InputProps={{ startAdornment: <InputAdornment position='start'><EmojiEventsIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: errors.direccion ? '#f44336' : 'gray', mb: 0.3 }}>Dirección *</Typography>
                                        <TextField
                                            value={form.direccion}
                                            onChange={(e) => setField('direccion', e.target.value)}
                                            error={errors.direccion}
                                            helperText={errors.direccion ? 'Requerido' : ''}
                                            fullWidth
                                            size='small'
                                            inputProps={{ maxLength: 60 }}
                                            InputProps={{ startAdornment: <InputAdornment position='start'><LocationOnIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ '& > div': { marginBottom: 0 } }}>
                                        <Typography sx={{ fontSize: 12, color: errors.cod_postal ? '#f44336' : 'gray', mb: 0.3 }}>
                                            Localidad / Código postal *
                                        </Typography>
                                        <SelectLocalidadesSoftland
                                            value={form.cod_postal}
                                            onChange={(e) => setField('cod_postal', e?.value || '')}
                                            isInvalid={errors.cod_postal}
                                        />
                                        {errors.cod_postal && (
                                            <Typography sx={{ fontSize: 11, color: '#f44336', mt: 0.3 }}>Requerido</Typography>
                                        )}
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: errors.latitud ? '#f44336' : 'gray', mb: 0.3 }}>Latitud</Typography>
                                        <TextField
                                            value={form.latitud}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/[^0-9.-]/g, '');
                                                const negative = raw.startsWith('-');
                                                const [int, ...dec] = raw.replace(/-/g, '').split('.');
                                                const num = dec.length ? `${int}.${dec.join('')}` : int;
                                                setField('latitud', negative ? `-${num}` : num);
                                            }}
                                            error={errors.latitud}
                                            helperText={errors.latitud ? 'Debe estar entre -55.1 y -21.7 (Argentina)' : 'Ej: -24.785427'}
                                            fullWidth
                                            size='small'
                                            inputProps={{ maxLength: 30 }}
                                            InputProps={{ startAdornment: <InputAdornment position='start'><ExploreIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: errors.longitud ? '#f44336' : 'gray', mb: 0.3 }}>Longitud</Typography>
                                        <TextField
                                            value={form.longitud}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/[^0-9.-]/g, '');
                                                const negative = raw.startsWith('-');
                                                const [int, ...dec] = raw.replace(/-/g, '').split('.');
                                                const num = dec.length ? `${int}.${dec.join('')}` : int;
                                                setField('longitud', negative ? `-${num}` : num);
                                            }}
                                            error={errors.longitud}
                                            helperText={errors.longitud ? 'Debe estar entre -73.6 y -53.6 (Argentina)' : 'Ej: -65.410034'}
                                            fullWidth
                                            size='small'
                                            inputProps={{ maxLength: 30 }}
                                            InputProps={{ startAdornment: <InputAdornment position='start'><ExploreIcon sx={{ fontSize: 16, color: 'gray' }} /></InputAdornment> }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                            <RouterIcon sx={{ fontSize: 14, color: errors.tecnologia ? '#f44336' : 'gray' }} />
                                            <Typography sx={{ fontSize: 12, color: errors.tecnologia ? '#f44336' : 'gray' }}>Tecnología *</Typography>
                                        </Box>
                                        <FormControl fullWidth size='small' error={errors.tecnologia}>
                                            <Select
                                                value={form.tecnologia}
                                                onChange={(e) => setField('tecnologia', e.target.value)}
                                                displayEmpty
                                                sx={{
                                                    height: 40,
                                                    borderRadius: 2,
                                                    color: isDarkMode ? '#fff' : '#000',
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: errors.tecnologia ? '#f44336' : 'gray' },
                                                }}
                                            >
                                                <MenuItem value='' disabled><em style={{ color: 'gray' }}>Seleccionar...</em></MenuItem>
                                                {TECNOLOGIA_OPTIONS.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                ))}
                                            </Select>
                                            {errors.tecnologia && <FormHelperText>Requerido</FormHelperText>}
                                        </FormControl>
                                    </Box>
                                </Grid>

                            </Grid>
                        </Paper>
                    </Grid>

                </Grid>
            </DialogContent>
            <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
            <DialogActions sx={{ justifyContent: 'center', gap: 1, p: 2 }}>
                <ButtonBasic
                    textButton='CANCELAR'
                    variant='wk'
                    color='nextStep'
                    onClick={handleClose}
                    isDisabled={isLoading}
                    sx={{ borderRadius: 2 }}
                />
                <ButtonBasic
                    textButton='CREAR EVENTUAL'
                    variant='contained'
                    color='success'
                    onClick={onSubmit}
                    isLoading={isLoading}
                    sx={{ borderRadius: 2 }}
                />
            </DialogActions>
        </Box>
    </Dialog>
  );
};

export default ModalCrearClienteEventual;
